import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trip, TripMeta, TripMember } from '../../types/trip';
import { useDesign } from '../../designs';
import { updateTripMeta, setTripMembers, isShareCodeTaken, saveCustomSlug, deleteTrip } from '../../services/trips';
import { getInitials } from '../onboarding/onboardingState';
import Modal from '../../components/ui/Modal';
import DateSpinner from '../../components/ui/DateSpinner';
import Input from '../../components/ui/Input';
import PlacesAutocomplete from '../../components/ui/PlacesAutocomplete';
import DesignButton from '../../components/ui/DesignButton';
import OrnamentalDivider from '../../components/ui/OrnamentalDivider';
import { logOut } from '../../services/auth';

interface SettingsModalProps {
  trip: Trip;
  tripId: string;
  isPlanner: boolean;
  claimedMemberUid: string | null;
  currentUid: string;
  onClose: () => void;
}

function formatDayStrip(startDate: string, endDate: string): string {
  if (!startDate || !endDate) return '';
  const days: string[] = [];
  const current = new Date(startDate + 'T00:00:00');
  const end = new Date(endDate + 'T00:00:00');
  while (current <= end) {
    days.push(current.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }));
    current.setDate(current.getDate() + 1);
  }
  return days.join(' · ');
}

interface MemberRowState {
  uid: string;
  displayName: string;
  initials: string;
  role: 'planner' | 'crew';
  email: string;
  phone: string;
  homeAddress: string;
  expanded: boolean;
}

function ShareLinkSection({ tripId, meta }: { tripId: string; meta: TripMeta }) {
  const design = useDesign();
  const d = design.tokens;
  const fs = design.typography.fontSize;
  const currentSlug = meta.customSlug || meta.shareCode;
  const [editing, setEditing] = useState(false);
  const [slug, setSlug] = useState(currentSlug);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const shareUrl = `${window.location.origin}/t/${currentSlug}`;

  const handleSaveSlug = async () => {
    const cleaned = slug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
    if (!cleaned || cleaned === currentSlug) { setEditing(false); setSlug(currentSlug); return; }
    setSaving(true); setError('');
    try {
      const taken = await isShareCodeTaken(cleaned);
      if (taken) { setError('Taken'); setSaving(false); return; }
      const oldSlug = meta.customSlug || meta.shareCode;
      await saveCustomSlug(tripId, cleaned, oldSlug, meta.shareCode);
      setEditing(false);
    } catch (err) { console.error('Failed to save slug:', err); setError('Error'); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ marginBottom: '20px' }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '10px 12px', backgroundColor: d.bgSurface,
        border: '1px solid rgba(200,146,58,0.10)', borderRadius: '2px',
      }}>
        {editing ? (
          <>
            <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, color: d.textSecondary, flexShrink: 0 }}>/t/</span>
            <input type="text" value={slug}
              onChange={(e) => { setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')); setError(''); }}
              onKeyDown={(e) => e.key === 'Enter' && handleSaveSlug()}
              onFocus={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.30)'; }}
              onBlur={(e) => { e.target.style.borderColor = 'rgba(200,146,58,0.10)'; }}
              autoFocus
              style={{
                flex: 1, fontFamily: "'Raleway', sans-serif", fontSize: fs.detail,
                color: d.textPrimary, backgroundColor: d.bgSurface,
                border: `1px solid ${error ? d.negative : 'rgba(200,146,58,0.30)'}`,
                borderRadius: '2px', padding: '4px 8px', outline: 'none', minWidth: 0,
              }}
            />
            {error && <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.metaLabel, color: d.negative }}>{error}</span>}
            <button onClick={handleSaveSlug} disabled={saving} style={{
              fontFamily: "'Raleway', sans-serif", fontSize: fs.button, fontWeight: 700,
              letterSpacing: '1.5px', textTransform: 'uppercase',
              color: d.confirmed, background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0,
            }}>{saving ? '...' : 'Save'}</button>
            <button onClick={() => { setEditing(false); setSlug(currentSlug); setError(''); }} style={{
              fontSize: fs.bodyInput, color: d.textSecondary, background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0,
            }}>×</button>
          </>
        ) : (
          <>
            <span style={{
              fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, color: d.textPrimary,
              flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
            }}>{shareUrl}</span>
            <button onClick={() => setEditing(true)} style={{
              fontFamily: "'Raleway', sans-serif", fontSize: fs.button, fontWeight: 700,
              letterSpacing: '1.5px', textTransform: 'uppercase',
              color: d.textTertiary, background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0,
            }}>Edit</button>
            <button onClick={async () => {
              try {
                await navigator.clipboard.writeText(shareUrl);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              } catch {
                // Fallback for browsers that block Clipboard API
                const ta = document.createElement('textarea');
                ta.value = shareUrl;
                ta.style.position = 'fixed';
                ta.style.opacity = '0';
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }
            }} style={{
              fontFamily: "'Raleway', sans-serif", fontSize: fs.button, fontWeight: 700,
              letterSpacing: '1.5px', textTransform: 'uppercase',
              color: copied ? d.confirmed : d.accent,
              background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0,
            }}>{copied ? 'Copied' : 'Copy'}</button>
          </>
        )}
      </div>
    </div>
  );
}

export default function SettingsModal({ trip, tripId, isPlanner, claimedMemberUid, currentUid, onClose }: SettingsModalProps) {
  const navigate = useNavigate();
  const design = useDesign();
  const d = design.tokens;
  const fs = design.typography.fontSize;
  const meta = trip.meta;
  const [tripName, setTripName] = useState(meta.tripName);
  const [destination, setDestination] = useState(meta.destination);
  const [occasion, setOccasion] = useState(meta.occasion || '');
  const [startDate, setStartDate] = useState(meta.startDate);
  const [endDate, setEndDate] = useState(meta.endDate);
  const [isSaving, setIsSaving] = useState(false);
  const [emailCopied, setEmailCopied] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const initialMembers: MemberRowState[] = Object.values(trip.members || {}).map((m) => ({
    uid: m.uid, displayName: m.displayName, initials: m.initials, role: m.role,
    email: m.email || '', phone: m.phone || '', homeAddress: m.homeAddress || '', expanded: false,
  }));
  const [members, setMembers] = useState<MemberRowState[]>(initialMembers);

  const updateMember = (index: number, updates: Partial<MemberRowState>) => {
    setMembers((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...updates };
      if (updates.displayName !== undefined) next[index].initials = getInitials(updates.displayName);
      return next;
    });
  };

  const addMember = () => {
    const uid = `crew_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    setMembers((prev) => [...prev, {
      uid, displayName: '', initials: '', role: 'crew',
      email: '', phone: '', homeAddress: '', expanded: true,
    }]);
  };

  const removeMember = (index: number) => {
    const name = members[index].displayName || 'this member';
    if (!window.confirm(`Remove ${name} from the crew?`)) return;
    setMembers((prev) => prev.filter((_, i) => i !== index));
  };

  const toggleRole = (index: number) => {
    updateMember(index, { role: members[index].role === 'planner' ? 'crew' : 'planner' });
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await updateTripMeta(tripId, {
        tripName: tripName.trim() || meta.tripName,
        destination: destination.trim() || meta.destination,
        ...(occasion.trim() ? { occasion: occasion.trim() } : {}),
        startDate, endDate,
      });
      const membersData: Record<string, TripMember> = {};
      members.forEach((m) => {
        if (!m.displayName.trim()) return;
        membersData[m.uid] = {
          uid: m.uid, displayName: m.displayName.trim(),
          initials: m.initials || getInitials(m.displayName), role: m.role,
          isAnonymous: false, joinedAt: Date.now(),
          ...(m.email ? { email: m.email } : {}),
          ...(m.phone ? { phone: m.phone } : {}),
          ...(m.homeAddress ? { homeAddress: m.homeAddress } : {}),
        };
      });
      await setTripMembers(tripId, membersData);
      onClose();
    } catch (err) {
      console.error('Failed to save settings:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const sectionHeader: React.CSSProperties = {
    fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 700,
    letterSpacing: '3px', textTransform: 'uppercase',
    color: d.accent, margin: '0 0 12px',
  };

  const divider: React.CSSProperties = {
    height: '1px', backgroundColor: 'rgba(200,146,58,0.07)', margin: '16px 0',
  };

  const dayStrip = formatDayStrip(startDate, endDate);

  return (
    <Modal title="Settings" onClose={onClose}>
      {/* ═══ TRIP DETAILS ═══ */}
      <h3 style={sectionHeader}>Trip Details</h3>

      {isPlanner && (
        <>
          <div style={{ display: 'flex', gap: '12px', marginBottom: '4px' }}>
            <div style={{ flex: 1 }}>
              <Input label="Trip Name" value={tripName} onChange={(e) => setTripName(e.target.value)} />
            </div>
            <div style={{ flex: 1 }}>
              <PlacesAutocomplete label="Destination" value={destination} onChange={setDestination} citiesOnly />
            </div>
          </div>
          <Input label="Occasion" value={occasion} onChange={(e) => setOccasion(e.target.value)} />
        </>
      )}

      <div style={{ display: 'flex', gap: '12px', marginBottom: '4px' }}>
        <DateSpinner label="Start Date" value={startDate} onChange={isPlanner ? setStartDate : () => {}} />
        <DateSpinner label="End Date" value={endDate} onChange={isPlanner ? setEndDate : () => {}} min={startDate} />
      </div>

      {dayStrip && (
        <p style={{
          fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300,
          color: d.textSecondary, margin: '8px 0 0', lineHeight: 1.5,
        }}>
          {dayStrip}
        </p>
      )}

      <div style={divider} />

      {/* ═══ CREW ═══ */}
      <h3 style={sectionHeader}>Crew</h3>

      {members.map((member, index) => (
        <div key={member.uid} style={{ marginBottom: '8px' }}>
          {/* Tappable header row */}
          <div
            onClick={() => updateMember(index, { expanded: !member.expanded })}
            style={{
              display: 'flex', alignItems: 'center', gap: '10px',
              padding: '8px 0', cursor: 'pointer',
            }}
          >
            {/* Initials badge */}
            <div style={{
              width: '32px', height: '32px', borderRadius: '50%',
              border: '1px solid rgba(200,146,58,0.30)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: "'Raleway', sans-serif", fontSize: fs.metaLabel, fontWeight: 600,
              color: d.textSecondary, flexShrink: 0,
            }}>
              {member.initials || '?'}
            </div>
            {/* Name and role */}
            <div style={{ flex: 1, minWidth: 0 }}>
              <span style={{
                fontFamily: "'Raleway', sans-serif", fontSize: fs.body, fontWeight: 500,
                color: d.textPrimary, display: 'block',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>
                {member.displayName || 'New member'}
              </span>
              <span style={{
                fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 700,
                letterSpacing: '1px', textTransform: 'uppercase',
                color: member.role === 'planner' ? d.accent : d.textSecondary,
              }}>
                {member.role}
              </span>
            </div>
          </div>

          {/* Expanded fields */}
          {member.expanded && (
            <div style={{ paddingLeft: '42px', paddingBottom: '8px' }}>
              {/* Name: planner can edit all, crew can edit their own */}
              <Input label="Name" value={member.displayName}
                onChange={(e) => updateMember(index, { displayName: e.target.value })} disabled={!isPlanner && member.uid !== claimedMemberUid} />
              {/* Contact info: planner can edit all, crew can only edit their own */}
              {(isPlanner || member.uid === claimedMemberUid) && (
                <>
                  <Input label="Email" type="email" value={member.email}
                    onChange={(e) => updateMember(index, { email: e.target.value })} />
                  <Input label="Phone" type="tel" value={member.phone}
                    onChange={(e) => updateMember(index, { phone: e.target.value })} />
                  <Input label="Home Address" value={member.homeAddress}
                    onChange={(e) => updateMember(index, { homeAddress: e.target.value })} />
                </>
              )}
              {isPlanner && (
                <div style={{ display: 'flex', gap: '8px', marginTop: '4px' }}>
                  <button onClick={(e) => { e.stopPropagation(); toggleRole(index); }} style={{
                    flex: 1, padding: '6px 12px',
                    fontFamily: "'Raleway', sans-serif", fontSize: fs.button, fontWeight: 700,
                    letterSpacing: '1.5px', textTransform: 'uppercase',
                    border: `1px solid rgba(200,146,58,0.20)`, borderRadius: '2px',
                    backgroundColor: member.role === 'planner' ? 'rgba(200,146,58,0.12)' : 'transparent',
                    color: member.role === 'planner' ? d.accent : d.textSecondary,
                    cursor: 'pointer',
                  }}>
                    {member.role === 'planner' ? 'Planner' : 'Make Planner'}
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); removeMember(index); }} style={{
                    padding: '6px 12px',
                    fontFamily: "'Raleway', sans-serif", fontSize: fs.button, fontWeight: 700,
                    letterSpacing: '1.5px', textTransform: 'uppercase',
                    border: `1px solid rgba(156,53,85,0.30)`, borderRadius: '2px',
                    backgroundColor: 'transparent', color: d.negative, cursor: 'pointer',
                  }}>
                    Remove
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}

      {/* Add person */}
      {isPlanner && (
        <button onClick={addMember} type="button" style={{
          width: '100%', padding: '10px 16px',
          fontFamily: "'Raleway', sans-serif", fontSize: fs.button, fontWeight: 700,
          letterSpacing: '2px', textTransform: 'uppercase',
          color: d.textSecondary, backgroundColor: 'transparent',
          border: '1px dashed rgba(200,146,58,0.15)', borderRadius: '2px',
          cursor: 'pointer', marginBottom: '16px',
        }}>
          + Add Person
        </button>
      )}

      <div style={divider} />

      {/* ═══ SHARING ═══ */}
      {meta.phase === 'shared' && meta.shareCode && (
        <>
          <h3 style={sectionHeader}>Sharing</h3>
          <ShareLinkSection tripId={tripId} meta={meta} />
        </>
      )}

      {/* Email Import */}
      {meta.inboundEmail && (
        <>
          <h3 style={sectionHeader}>Email Import</h3>
          <p style={{
            fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300,
            color: d.textSecondary, marginBottom: '8px',
          }}>
            Forward confirmations to auto-capture travel details.
          </p>
          <div style={{
            display: 'flex', alignItems: 'center', gap: '8px',
            padding: '10px 12px', backgroundColor: d.bgSurface,
            border: '1px solid rgba(200,146,58,0.10)', borderRadius: '2px',
            marginBottom: '16px',
          }}>
            <span style={{
              fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 400,
              color: d.textPrimary, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
            }}>
              {meta.inboundEmail}
            </span>
            <button onClick={async () => {
              try {
                await navigator.clipboard.writeText(meta.inboundEmail || '');
                setEmailCopied(true);
                setTimeout(() => setEmailCopied(false), 2000);
              } catch {
                const ta = document.createElement('textarea');
                ta.value = meta.inboundEmail || '';
                ta.style.position = 'fixed';
                ta.style.opacity = '0';
                document.body.appendChild(ta);
                ta.select();
                document.execCommand('copy');
                document.body.removeChild(ta);
                setEmailCopied(true);
                setTimeout(() => setEmailCopied(false), 2000);
              }
            }} style={{
              fontFamily: "'Raleway', sans-serif", fontSize: fs.button, fontWeight: 700,
              letterSpacing: '1.5px', textTransform: 'uppercase',
              color: emailCopied ? d.confirmed : d.accent,
              background: 'none', border: 'none', cursor: 'pointer',
            }}>
              {emailCopied ? 'Copied' : 'Copy'}
            </button>
          </div>
        </>
      )}

      {isPlanner && (
        <DesignButton tier={2} fullWidth onClick={handleSave} disabled={isSaving}>
          {isSaving ? 'Saving...' : 'Save'}
        </DesignButton>
      )}

      <OrnamentalDivider style={{ margin: '20px 0 16px' }} />

      <button onClick={() => { onClose(); navigate('/'); }} type="button" style={{
        display: 'block', width: '100%', padding: '8px',
        fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 400,
        color: d.textSecondary, background: 'none', border: 'none',
        cursor: 'pointer', textAlign: 'center',
      }}>
        Return to Trips
      </button>

      <button onClick={async () => { await logOut(); navigate('/login'); }} type="button" style={{
        display: 'block', width: '100%', marginTop: '4px', padding: '8px',
        fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 400,
        color: d.textSecondary, background: 'none', border: 'none',
        cursor: 'pointer', textAlign: 'center',
      }}>
        Sign Out
      </button>

      {isPlanner && (
        <button onClick={() => setShowDeleteConfirm(true)} type="button" style={{
          display: 'block', width: '100%', marginTop: '16px', padding: '8px',
          fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 400,
          color: d.negative, background: 'none', border: 'none',
          cursor: 'pointer', textAlign: 'center',
        }}>
          Delete Trip
        </button>
      )}

      {showDeleteConfirm && (
        <Modal title="Delete this trip?" onClose={() => setShowDeleteConfirm(false)}>
          <p style={{
            fontFamily: "'Raleway', sans-serif", fontSize: fs.bodyInput, fontWeight: 300,
            color: d.textSecondary, lineHeight: 1.6, margin: '0 0 16px',
          }}>
            This will permanently remove{' '}
            <span style={{ fontWeight: 600, color: d.textPrimary }}>{meta.tripName}</span>
            {' '}for you and your crew. This cannot be undone.
          </p>

          {meta.isPaid && (
            <p style={{
              fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300,
              color: d.negative, lineHeight: 1.5, margin: '0 0 20px',
            }}>
              This trip has been shared with your crew. They will lose access.
            </p>
          )}

          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={() => setShowDeleteConfirm(false)} type="button" style={{
              flex: 1, padding: '11px 16px', borderRadius: '2px',
              border: `1px solid ${d.border}`, backgroundColor: 'transparent',
              fontFamily: "'Raleway', sans-serif", fontSize: fs.button, fontWeight: 700,
              letterSpacing: '3px', textTransform: 'uppercase',
              color: d.textSecondary, cursor: 'pointer',
            }}>
              Cancel
            </button>
            <button
              onClick={async () => {
                setIsDeleting(true);
                try {
                  await deleteTrip(tripId, currentUid);
                  onClose();
                  navigate('/');
                } catch (err) {
                  console.error('Failed to delete trip:', err);
                  setIsDeleting(false);
                }
              }}
              disabled={isDeleting}
              type="button"
              style={{
                padding: '11px 16px', borderRadius: '2px',
                border: 'none', backgroundColor: d.negative,
                fontFamily: "'Raleway', sans-serif", fontSize: fs.button, fontWeight: 700,
                letterSpacing: '3px', textTransform: 'uppercase',
                color: d.textPrimary, cursor: isDeleting ? 'not-allowed' : 'pointer',
                opacity: isDeleting ? 0.5 : 1,
              }}
            >
              {isDeleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </Modal>
      )}
    </Modal>
  );
}
